import React, { useState, useEffect } from "react";
import { Card, Form, Button, Modal, Row, Col, Alert } from "react-bootstrap";
import {
  getAllInfluencers,
  updateInfluencer,
  deleteInfluencer,
  createInfluencer,
} from "api/apis";

const Influencers = () => {
  const [influencers, setInfluencers] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentInfluencer, setCurrentInfluencer] = useState<any>({
    name: "",
    platform: "",
    hyperlink: "",
    video: null,
    videoUrl: "",
  });

  // Fetch all influencers on component mount
  useEffect(() => {
    fetchInfluencers();
  }, []);

  const fetchInfluencers = async () => {
    try {
      const data = await getAllInfluencers();
      setInfluencers(data);
    } catch (error) {
      console.error("Failed to fetch influencers", error);
    }
  };

  const confirmDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteInfluencer(deleteId);
      setInfluencers(influencers.filter((inf) => inf._id !== deleteId));
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Failed to delete influencer", error);
    }
  };

  const handleEdit = (influencer: any) => {
    setIsEditMode(true);
    setCurrentInfluencer({
      _id: influencer._id, // Make sure to include the ID
      name: influencer.name,
      platform: influencer.platform,
      hyperlink: influencer.hyperlink,
      videoUrl: influencer.videoUrl,
      video: null, // Reset video file but keep URL for preview
    });
    setShowModal(true);
  };
  const handleAddInfluencer = () => {
    setIsEditMode(false);
    setCurrentInfluencer({
      name: "",
      platform: "",
      hyperlink: "",
      video: null,
      videoUrl: "",
    });
    setShowModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentInfluencer({
      ...currentInfluencer,
      [name]: value,
    });
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCurrentInfluencer({
        ...currentInfluencer,
        video: e.target.files[0],
        videoUrl: URL.createObjectURL(e.target.files[0]),
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const formData = new FormData();

      // Always include these fields in the form data
      formData.append("name", currentInfluencer.name);
      formData.append("platform", currentInfluencer.platform);
      formData.append("hyperlink", currentInfluencer.hyperlink || ""); // Ensure hyperlink is never undefined

      // Handle file upload
      if (currentInfluencer.video) {
        if (isEditMode) {
          formData.append("video", currentInfluencer.video);
        } else {
          formData.append("file", currentInfluencer.video);
        }
      }

      if (isEditMode && currentInfluencer._id) {
        await updateInfluencer(currentInfluencer._id, formData);
      } else {
        await createInfluencer(formData);
      }

      setShowModal(false);
      fetchInfluencers();
    } catch (error) {
      console.error("Failed to save influencer", error);
    }
  };

  return (
    <div
      className="container"
      style={{ marginTop: "8rem", paddingBottom: "4rem" }}
    >
      <div className="d-flex justify-content-between mb-4">
        <h2>Influencers Management</h2>
        <Button variant="primary" onClick={handleAddInfluencer}>
          Add New Influencer
        </Button>
      </div>

      {/* Fixed 3-column grid */}
      <Row className="g-4">
        {influencers.map((influencer) => (
          <Col key={influencer._id} md={4}>
            <Card className="h-100">
              <div className="ratio ratio-16x9">
                {influencer.videoUrl && (
                  <video controls style={{ objectFit: "cover" }}>
                    <source src={influencer.videoUrl} type="video/mp4" />
                  </video>
                )}
              </div>
              <Card.Body className="d-flex flex-column">
                <Card.Title>{influencer.name}</Card.Title>
                <Card.Text className="flex-grow-1">
                  <strong>Platform:</strong> {influencer.platform}
                  <br />
                  <strong>Link:</strong>{" "}
                  <a
                    href={influencer.hyperlink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: "100%" }}
                  >
                    {influencer.hyperlink}
                  </a>
                </Card.Text>
                <div className="mt-auto pt-2 border-top">
                  <Button
                    variant="primary"
                    size="sm"
                    className="me-2"
                    onClick={() => handleEdit(influencer)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => confirmDelete(influencer._id)}
                  >
                    Delete
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Edit Influencer" : "Add New Influencer"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="influencerName" className="mb-3">
              <Form.Label>Name*</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={currentInfluencer.name}
                onChange={handleInputChange}
                placeholder="Enter influencer name"
                required
              />
            </Form.Group>

            <Form.Group controlId="influencerPlatform" className="mb-3">
              <Form.Label>Platform*</Form.Label>
              <Form.Control
                type="text"
                name="platform"
                value={currentInfluencer.platform}
                onChange={handleInputChange}
                placeholder="Enter platform (e.g., Instagram, YouTube)"
                required
              />
            </Form.Group>

            <Form.Group controlId="influencerHyperlink" className="mb-3">
              <Form.Label>Hyperlink*</Form.Label>
              <Form.Control
                type="url"
                name="hyperlink"
                value={currentInfluencer.hyperlink}
                onChange={handleInputChange}
                placeholder="Enter hyperlink URL"
                required
              />
            </Form.Group>

            <Form.Group controlId="influencerVideo" className="mb-3">
              <Form.Label>
                {isEditMode ? "New Video (optional)" : "Video*"}
              </Form.Label>
              <Form.Control
                type="file"
                accept="video/*"
                onChange={handleVideoChange}
                required={!isEditMode} // Required only for create
              />
              {currentInfluencer.videoUrl && (
                <div className="mt-2 ratio ratio-16x9">
                  <video controls style={{ maxHeight: "200px" }}>
                    <source src={currentInfluencer.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
              {isEditMode && !currentInfluencer.video && (
                <div className="text-muted mt-2">
                  Current video will be kept if no new video is selected
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {isEditMode ? "Update" : "Add"} Influencer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            Are you sure you want to delete this influencer? This action cannot
            be undone.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Influencers;
